import { CustomerMapParsed, MatchboiError, SupportedTools } from '../interfaces'

import { CCMCategoryKey, DefaultTool, Tool } from './base'
import { CarbonBlackResponseTool } from './carbonblack-response.tool'
import { CarbonBlackCloudInvestigateTool } from './carbonblackcloud-investigate.tool'
import { CorelightInvestigatorTool } from './corelight-investigator'
import { CrowdStrikeLogScaleTool } from './crowdstrike-logscale.tool'
import { CrowdStrikeTool } from './crowdstrike.tool'
import { ElasticTool } from './elastic.tool'
import { GoogleSecOpsTool } from './google-secops'
import {
  CarbonBlackCloudLiveQueryTool,
  ElasticSignalTool,
  SnortTool,
  YaraTool,
} from './metadata-only.tools'
import { MicrosoftDefenderTool } from './microsoft-defender.tool'
import { MicrosoftSentinelTool } from './microsoft-sentinel.tool'
import { PaloAltoCortexXDRTool } from './paloalto-cortex-xdr.tool'
import { QRadarTool } from './qradar.tool'
import { SentinelOnePowerQueryTool } from './sentinelone-powerquery.tool'
import { SentinelOneSingularityTool } from './sentinelone-singularity.tool'
import { SentinelOneTool } from './sentinelone.tool'
import { SplunkTool } from './splunk.tool'
import { TaniumSignalTool } from './tanium-signal.tool'
import { TaniumTool } from './tanium.tool'
import { TrendMicroVisionOneTool } from './trendmicro-visionone.tool'

export { Tool }

// TODO: auto discover from local recursive directory search for Tool subclasses
const allToolsList: Array<new () => Tool> = [
  SplunkTool,
  ElasticTool,
  ElasticSignalTool,
  CrowdStrikeTool,
  CrowdStrikeLogScaleTool,
  CarbonBlackResponseTool,
  CarbonBlackCloudInvestigateTool,
  CarbonBlackCloudLiveQueryTool,
  CorelightInvestigatorTool,
  MicrosoftDefenderTool,
  MicrosoftSentinelTool,
  QRadarTool,
  SentinelOneTool,
  SentinelOneSingularityTool,
  SentinelOnePowerQueryTool,
  SnortTool,
  TrendMicroVisionOneTool,
  PaloAltoCortexXDRTool,
  TaniumTool,
  TaniumSignalTool,
  GoogleSecOpsTool,
  YaraTool,
].sort((a, b) => a.name.localeCompare(b.name))

/* Tool mapping by name to respective Tool instance*/
export const toolsMap: {
  [key in SupportedTools]?: Tool
} = Object.fromEntries(
  allToolsList.map((toolClass) => {
    const tool = new toolClass()
    return [tool.toolName, tool]
  }),
)

/**
 * Get instance of tool by name, defaulting to base Tool when not available
 * @param toolName Target tool name
 */
export const toolFactory = (toolName: SupportedTools): Tool => {
  return toolsMap[toolName] ?? new DefaultTool()
}

/**
 * UI-friendly view over the tool registry: one entry per registered Tool with the metadata
 * fields needed for the Settings → Environment and custom-hunt-package dropdowns.
 *
 * `category` is the uppercased `toolKind` ('siem' → 'SIEM', etc.) and is the user-facing
 * grouping label. Replaces the old hand-maintained `SupportedMappings` array (HUN-1195).
 */
export interface ToolMapping {
  name: SupportedTools
  category: string
  customMapping: boolean
  toolSpecific?: string[]
  allowedCategories?: CCMCategoryKey[]
}

export const toolMappings: ToolMapping[] = Object.values(toolsMap).map(
  (tool) => ({
    name: tool.toolName,
    category: tool.toolKind.toUpperCase(),
    customMapping: tool.customMapping,
    toolSpecific: tool.toolSpecific,
    allowedCategories: tool.allowedCategories,
  }),
)

/**
 * Parse exported tool field mapping into the common parsed map format, or throws error if not supported
 *
 * @param toolExportContents Contents from the tool field mapping export
 * @param toolName Select which tool the export came from to use the appropriate parser
 */
export const processMapUpload = (
  toolExportContents: string,
  toolName: SupportedTools,
): CustomerMapParsed => {
  const tool = toolFactory(toolName)

  if (!tool.supportsMapUploads())
    throw new MatchboiError(`Tool "${toolName}" does not support map uploads`)

  return tool.parseMapUpload(toolExportContents)
}
